import React, { FC, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client/react';
import LoadingSpinner from '../components/LoadingSpinner';
import { MainContentPanel } from '../components/MainContentPanel';
import { formatDate, numberAsKr } from '../Formatters';
import { CANCEL_PRICE_UPDATE, GET_CHANGES, GET_SUBSCRIPTIONS } from './subscriptions.graphql';
import { Alert } from '@mui/material';
import { ChangeLog } from '../components/ChangeLog';
import { useConfig } from '../components/Configuration';
import { Change, SubscriptionModel, SubscriptionPrice } from '../interfaces';
import { UpdatePriceDialog } from './UpdatePriceDialog';
import { PdfPriceCard } from '../pdf/overview';
import { HistoricalPriceCard } from '../historical/overview';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

export const Overview: FC = () => {
  return (
    <MainContentPanel>
      <Stack spacing={5}>
        <SubscriptionsCard />
        <PdfPriceCard />
        <HistoricalPriceCard />
        <Divider />
        <ChangeLogCard />
      </Stack>
    </MainContentPanel>
  );
};

const SubscriptionsCard: FC = () => {
  const { loading, data } = useQuery<{ subscriptions: SubscriptionModel[] }>(GET_SUBSCRIPTIONS);

  return (
    <Stack>
      <Typography variant="h5" gutterBottom>
        Abonnement
      </Typography>
      {loading && <LoadingSpinner />}
      {data && <SubscriptionTable subscriptions={data.subscriptions} />}
    </Stack>
  );
};

function SubscriptionTable({ subscriptions }: { subscriptions: SubscriptionModel[] }) {
  const equalColumn = {
    minWidth: 160,
    flex: 1,
    align: 'left' as const,
    headerAlign: 'left' as const,
  };

  const columns: GridColDef<SubscriptionModel>[] = [
    {
      field: 'name',
      headerName: 'Navn',
      ...equalColumn,
      valueGetter: (_, row) => row.name,
    },
    {
      field: 'singleUser',
      headerName: '1 bruker',
      ...equalColumn,
      renderCell: (params) => <Price subscription={params.row} priceGetter={(p) => p.singleUser} newPriceGetter={(p) => p.singleUser} />,
    },
    {
      field: 'extraUser',
      headerName: 'Ekstra brukere',
      ...equalColumn,
      renderCell: (params) => <Price subscription={params.row} priceGetter={(p) => p.extraUser} newPriceGetter={(p) => p.extraUser} />,
    },
    {
      field: 'actions',
      headerName: 'Handling',
      ...equalColumn,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => <SubscriptionAction subscription={params.row} />,
    },
  ];

  return (
    <Grid container>
      <Grid sx={{ width: '100%' }}>
        <Box sx={{ width: '100%' }}>
          <DataGridPro
            autoHeight
            density="compact"
            rows={subscriptions}
            columns={columns}
            getRowId={(row) => row.id}
            localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
            pagination
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 50,
                  page: 0,
                },
              },
            }}
            sx={{
              width: '100%',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}

function Price({
  subscription,
  priceGetter,
  newPriceGetter,
}: {
  subscription: SubscriptionModel;
  priceGetter(p: SubscriptionPrice): number;
  newPriceGetter(p: SubscriptionPrice): number;
}) {
  const price = subscription.price;
  if (!price) return null;
  if (!subscription.pendingPriceChange) return <span>{numberAsKr(priceGetter(price))}</span>;
  return (
    <>
      <OldPriceSpan>{numberAsKr(priceGetter(price))}</OldPriceSpan>
      <NewPriceSpan>{numberAsKr(newPriceGetter(subscription.pendingPriceChange.newPrice))}</NewPriceSpan>
    </>
  );
}

const OldPriceSpan = styled('span')(({ theme }) => ({
  textDecoration: 'line-through',
  color: theme.palette.error.main,
}));

const NewPriceSpan = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const SubscriptionAction: FC<{ subscription: SubscriptionModel }> = ({ subscription }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cancelUpdate, { loading }] = useMutation<any, any>(CANCEL_PRICE_UPDATE, { variables: { input: { subscriptionId: subscription.bksId } } });
  if (!subscription.price) return null;

  if (subscription.pendingPriceChange) {
    return (
      <Stack direction="row" sx={{alignItems: 'center'}}>
        <div>
          <IconButton disabled={loading} title="Forkast endring" onClick={() => cancelUpdate()} size="small">
            <Delete fontSize="small" />
          </IconButton>
        </div>
        <Alert
          sx={{
            display: 'inline-flex',
            verticalAlign: 'middle',
            pt: 0,
            pb: 0,
            '& .MuiAlert-message': {
              padding: 0,
            },
            '& .MuiAlert-icon': {
              padding: 0,
            },
          }}
          severity="info">
          Prisen vil bli oppdatert {formatDate(subscription.pendingPriceChange.timeOfChange)}
        </Alert>
      </Stack>
    );
  } else {
    return (
      <>
        <IconButton disabled={busy} title="Oppdater prisen" onClick={() => setOpen(true)} size="small">
          <Edit fontSize="small" />
        </IconButton>
        {open && <UpdatePriceDialog subscription={subscription} onClose={() => setOpen(false)} setBusy={setBusy} />}
        {busy && (
          <CircularProgress
            sx={{
              verticalAlign: `middle`,
            }}
            size={30}
          />
        )}
      </>
    );
  }
};

function ChangeLogCard() {
  const config = useConfig();
  const [limit, setLimit] = useState(5);
  const pollIntervall = config ? config.defaultPollingInterval : 0;

  const { loading, data, refetch } = useQuery<{ queryResult: { totalCount: number; changes: Change[] } }>(GET_CHANGES, {
    variables: { limit },
    pollInterval: pollIntervall,
  });
  const moreToLoad = data && data.queryResult.changes && data.queryResult.changes.length < data.queryResult.totalCount;

  const doFetchMore = () => {
    if (!data) return;
    setLimit(limit + 5);
    refetch();
  };

  return (
    <Stack>
      <Typography variant="h5" gutterBottom>
        Endringer
      </Typography>
      {!data && loading && <LoadingSpinner />}
      <Grid container>
        <Grid>
          {data && <ChangeLog changes={data.queryResult.changes} />}
          {moreToLoad && (
            <Button disabled={loading} onClick={doFetchMore}>
              Last flere
            </Button>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
}
